import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy, ListPlus, Sparkles } from 'lucide-react';
import { ListenButton } from '@/components/ListenButton';
import { ShareAnswerMenu } from '@/components/ai/ShareAnswerMenu';
import { AnswerTasks } from '@/components/ai/AnswerTasks';
import { TaskCardStrip } from '@/components/ai/TaskCardStrip';
import { TaskPeekDialog } from '@/components/ai/TaskPeekDialog';
import { RemindMenu } from '@/components/ai/RemindMenu';
import { linkTaskTitles } from '@/utils/markdownText';

const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

// One chat turn. Assistant text renders as Markdown with linked task chips; while streaming a caret blinks at the end.
export const ChatMessage = ({ message, modelName, streaming = false, userName, question, teams = [], contacts = [], teamId, user, chatId, onReminderCreated }) => {
  const [copied, setCopied] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [cards, setCards] = useState(message.tasks || []);
  const [peekId, setPeekId] = useState(null);
  const mine = message.role === 'user';
  const closeTasks = useCallback(() => setTasksOpen(false), []);

  useEffect(() => { setCards(message.tasks || []); }, [message.tasks]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const patchCard = useCallback((patch) => setCards((prev) => prev.map((c) => (c.id === patch.id ? { ...c, ...patch } : c))), []);
  const linked = useMemo(() => (mine || streaming ? message.content : linkTaskTitles(message.content, cards)), [mine, streaming, message.content, cards]);
  const components = useMemo(() => ({
    a: ({ href, children }) => (href?.startsWith('#task:')
      ? <button type="button" className="chat-task-chip" onClick={() => setPeekId(href.slice(6))} data-testid={`chat-task-chip-${href.slice(6)}`}>{children}</button>
      : <a href={href} target="_blank" rel="noreferrer">{children}</a>),
  }), []);

  const showActions = !mine && !streaming && message.content && !message.local;
  const draftTeam = teamId || teams[0]?.id;

  return (
    <div className={`chat-turn ${mine ? 'mine' : 'theirs'} ${streaming ? 'streaming' : ''}`} data-testid={`chat-message-${message.id}`}>
      {!mine && <div className="chat-avatar" aria-hidden="true"><Sparkles size={15} /></div>}
      <div className="chat-turn-body">
        <div className="chat-turn-meta">
          <span className="chat-turn-author">{mine ? userName || 'You' : modelName || 'Assistant'}</span>
          {message.created_at && <span className="chat-turn-time">{timeOf(message.created_at)}</span>}
        </div>
        <div className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
          {mine ? (
            <p className="chat-user-text">{message.content}</p>
          ) : (
            <div className="chat-markdown" data-testid={streaming ? 'chat-streaming-text' : undefined}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{linked || (streaming ? '' : '…')}</ReactMarkdown>
              {streaming && <span className="chat-caret" aria-hidden="true" />}
            </div>
          )}
        </div>
        {!mine && !streaming && <TaskCardStrip cards={cards} onOpen={setPeekId} onMoved={patchCard} />}
        {showActions && (
          <div className="chat-turn-actions">
            <ListenButton id={`chat-${message.id}`} text={message.content} compact testId={`chat-listen-${message.id}`} />
            <button type="button" className="chat-action" onClick={copy} aria-label="Copy answer" title="Copy" data-testid={`chat-copy-${message.id}`}>
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
            {draftTeam && (
              <button type="button" className={`chat-action with-label ${tasksOpen ? 'active' : ''}`} onClick={() => setTasksOpen((o) => !o)} aria-pressed={tasksOpen} aria-label="Make tasks from this answer" title="Make tasks from this answer" data-testid={`chat-make-tasks-${message.id}`}>
                <ListPlus size={13} /> <span>Make tasks</span>
              </button>
            )}
            <RemindMenu message={message} chatId={chatId} onCreated={onReminderCreated} />
            <ShareAnswerMenu message={message} modelName={modelName} question={question} teams={teams} contacts={contacts} user={user} />
          </div>
        )}
        {tasksOpen && draftTeam && (
          <AnswerTasks answer={message.content} teamId={draftTeam} teams={teams} user={user} onClose={closeTasks} messageId={message.id} />
        )}
        {peekId && <TaskPeekDialog taskId={peekId} onClose={() => setPeekId(null)} onChanged={patchCard} />}
      </div>
    </div>
  );
};
